/** German strings for the shared UI shells, audio, tuning, career data and client errors. Keys are the exact English source text. */
export const DE_UI: Readonly<Record<string, string>> = {
  // Audio mixer
  'SOUND ON': 'SOUND AN',
  'SOUND OFF': 'SOUND AUS',
  'Press M in a race to mute': 'Im Rennen mit M stummschalten',
  'Output level': 'Ausgangspegel',
  '{channel} volume': 'Lautstärke {channel}',
  'DYNAMIC RANGE': 'DYNAMIKUMFANG',
  'Dynamic range': 'Dynamikumfang',
  'PLAY TEST SOUND': 'TESTTON ABSPIELEN',
  'OFF': 'AUS',
  'MASTER': 'GESAMT',
  'ENGINE + EFFECTS': 'MOTOR + EFFEKTE',
  'MUSIC': 'MUSIK',
  'INTERFACE': 'OBERFLÄCHE',
  'Everything you hear': 'Alles, was du hörst',
  'Engines, tyres, impacts, weather': 'Motoren, Reifen, Crashs, Wetter',
  'Adaptive race score and menu theme': 'Dynamischer Rennsound und Menüthema',
  'Menu clicks, rewards and race calls': 'Menüklicks, Belohnungen und Rennansagen',
  'WIDE': 'WEIT',
  'STANDARD': 'STANDARD',
  'NIGHT': 'NACHT',
  'Full punch for headphones and home cinema.': 'Voller Druck für Kopfhörer und Heimkino.',
  'Balanced for most speakers and headsets.': 'Ausgewogen für die meisten Lautsprecher und Headsets.',
  'Evens out loud crashes; clearer on laptops and late at night.': 'Dämpft laute Crashs – klarer auf Laptops und spät in der Nacht.',
  // Connection + start lights
  'LINK': 'VERBINDUNG',
  'Excellent connection': 'Exzellente Verbindung',
  'Good connection': 'Gute Verbindung',
  'Fair connection': 'Mäßige Verbindung',
  'Poor connection': 'Schwache Verbindung',
  'Reconnecting': 'Verbinde neu',
  'GO': 'LOS',
  // Spectator shell
  'CONNECTING': 'VERBINDE',
  'INVITE ↩': 'EINLADUNG ↩',
  'Private Race': 'Privates Rennen',
  'CONNECTING TO GRID': 'VERBINDE MIT STARTAUFSTELLUNG',
  '{drivers} DRIVERS · {watching} WATCHING': '{drivers} FAHRER · {watching} ZUSCHAUER',
  'CLAIM GRID SLOT': 'STARTPLATZ SICHERN',
  'LIVE TRACK MAP': 'LIVE-STRECKENKARTE',
  'POSITION FEED': 'POSITIONEN',
  'LOW-BANDWIDTH LIVE VIEW': 'LIVE-ANSICHT MIT WENIG DATEN',
  'RACE ORDER': 'RENNREIHENFOLGE',
  'AUTO REFRESH': 'AUTO-UPDATE',
  'READ-ONLY PIT WALL': 'BOXENMAUER · NUR ZUSEHEN',
  'Live timing and positions continue through brief mobile network gaps.': 'Live-Zeiten und Positionen laufen auch bei kurzen Funklöchern weiter.',
  // Race shell: top bar, prompts, invite
  'GAMEPAD': 'GAMEPAD',
  'LANDSCAPE READY': 'QUERFORMAT BEREIT',
  'FULLSCREEN': 'VOLLBILD',
  'SETTINGS': 'EINSTELLUNGEN',
  'EXIT ×': 'BEENDEN ×',
  'Enter fullscreen': 'Vollbild aktivieren',
  'Open settings': 'Einstellungen öffnen',
  'Toggle sound': 'Sound umschalten',
  'Exit race': 'Rennen beenden',
  'LOADING RACE ENGINE': 'LADE RENN-ENGINE',
  'RESTORING GRAPHICS': 'GRAFIK WIRD WIEDERHERGESTELLT',
  'Your race is safe. Rebuilding the WebGL renderer…': 'Dein Rennen ist sicher. WebGL-Renderer wird neu aufgebaut …',
  'PHONE STEERING': 'HANDY-LENKUNG',
  'DRIVE WITH<br>TILT.': 'LENK MIT<br>NEIGUNG.',
  'Hold your phone or tablet naturally and tilt it to steer. Throttle, brake, drift and nitro stay on screen.': 'Halte dein Handy oder Tablet ganz natürlich und neige es zum Lenken. Gas, Bremse, Drift und Nitro bleiben auf dem Bildschirm.',
  'ENABLE TILT STEERING →': 'NEIGUNGSLENKUNG AN →',
  'USE STEERING SWIPER': 'LENK-SLIDER NUTZEN',
  'Your browser requires one tap before sharing motion-sensor data.': 'Dein Browser braucht einen Tipp, bevor er Bewegungssensordaten freigibt.',
  'PRIVATE GRID ACCESS': 'PRIVATER ZUGANG ZUM GRID',
  'BRING YOUR CREW.': 'HOL DEINE CREW.',
  'Close invite sharing': 'Einladung schließen',
  'CLOSE': 'SCHLIESSEN',
  'QR code containing the private race invite link': 'QR-Code mit dem privaten Einladungslink',
  'SCAN WITH A PHONE CAMERA': 'MIT DER HANDYKAMERA SCANNEN',
  'ONE LINK // ONE GRID': 'EIN LINK // EIN GRID',
  'JOIN THE<br>NIGHT SHIFT.': 'AB IN DIE<br>NACHTSCHICHT.',
  'Scan the QR code or send the private link. Anyone who has it can claim an open grid slot before launch.': 'Scanne den QR-Code oder schick den privaten Link. Wer ihn hat, kann sich vor dem Start einen freien Startplatz sichern.',
  'PRIVATE INVITE LINK': 'PRIVATER EINLADUNGSLINK',
  'COPY LINK': 'LINK KOPIEREN',
  'SHARE…': 'TEILEN …',
  'Generated locally. Your invite is never sent to a QR service.': 'Lokal erzeugt. Deine Einladung geht nie an einen QR-Dienst.',
  // Race HUD
  'CIRCUIT RACE': 'RUNDSTRECKE',
  'LAP': 'RUNDE',
  'Damage by side: none': 'Schaden pro Seite: keiner',
  'INTEGRITY': 'ZUSTAND',
  'PERSONAL BEST': 'PERSÖNLICHE BESTZEIT',
  'GHOST STANDBY': 'GHOST BEREIT',
  'GOLD TARGET': 'GOLD-ZIEL',
  'BUILD THE CHAIN': 'BAU DIE KETTE AUF',
  'STEER / GRIP': 'LENKUNG / GRIP',
  'SKILL COMEBACK': 'SKILL-COMEBACK',
  'GEAR': 'GANG',
  'NITRO': 'NITRO',
  'SLIPSTREAM': 'WINDSCHATTEN',
  'COOL-DOWN LAP': 'AUSLAUFRUNDE',
  'RACE CONTROL DRIVING': 'RENNLEITUNG FÄHRT',
  'LAUNCH CONTROL': 'LAUNCH CONTROL',
  'BUILD': 'AUFBAUEN',
  'LIVE CONTRACT': 'LIVE-AUFTRAG',
  'OPTIONAL OBJECTIVE': 'OPTIONALES ZIEL',
  'SECTOR DUEL': 'SEKTORDUELL',
  'PURSUIT STATUS': 'VERFOLGUNGSSTATUS',
  'LOW PROFILE': 'UNAUFFÄLLIG',
  'SET UP': 'VORBEREITEN',
  'SMOOTH INPUT': 'SANFT EINLENKEN',
  'DRIVE': 'FAHREN',
  'DRIFT': 'DRIFT',
  'BANK': 'SICHERN',
  'TURRET': 'TURM',
  'FIRE': 'FEUER',
  'RECOVER': 'ZURÜCKSETZEN',
  'CAMERA': 'KAMERA',
  'RESTART': 'NEUSTART',
  'PAUSE': 'PAUSE',
  'STEER': 'LENKEN',
  'GAS': 'GAS',
  'BRAKE': 'BREMSE',
  // Touch controls
  'Touch driving controls': 'Touch-Fahrsteuerung',
  'Steering': 'Lenkung',
  'Steering swiper: slide left or right, release to centre': 'Lenk-Slider: nach links oder rechts wischen, loslassen zentriert',
  'Pedals and race actions': 'Pedale und Rennaktionen',
  'Tap to rotate, hold to drift and earn exit nitro': 'Tippen zum Eindrehen, halten zum Driften – bringt Nitro am Kurvenausgang',
  'Nitro boost, 100 percent available': 'Nitro-Boost, 100 Prozent verfügbar',
  'Brake': 'Bremse',
  'Go, accelerate': 'Los, Gas geben',
  'Mobile race shortcuts': 'Mobile Renn-Shortcuts',
  'Cockpit view': 'Cockpit-Ansicht',
  'Current camera: chase': 'Aktuelle Kamera: Verfolger',
  'Change camera. Current view: chase': 'Kamera wechseln. Aktuelle Ansicht: Verfolger',
  'CHASE': 'VERFOLGER',
  'Bank Heat Run score': 'Heat-Run-Punkte sichern',
  'Bank completed Heat Run score': 'Abgeschlossene Heat-Run-Punkte sichern',
  'Recover to a safe line': 'Auf sichere Linie zurücksetzen',
  'Restart time trial': 'Zeitfahren neu starten',
  'TILT MODE': 'NEIGUNGSMODUS',
  'TAP FOR SWIPER': 'TIPPEN FÜR SLIDER',
  'Touch tank controls': 'Touch-Panzersteuerung',
  'Rotate turret left': 'Turm nach links drehen',
  'Fire cannon': 'Kanone abfeuern',
  'Rotate turret right': 'Turm nach rechts drehen',
  // Settings sheet
  'RACE SYSTEMS': 'RENNSYSTEME',
  'Settings sections': 'Einstellungsbereiche',
  'VIDEO': 'GRAFIK',
  'AUDIO': 'AUDIO',
  'CONTROLS': 'STEUERUNG',
  'DRIVING': 'FAHREN',
  'TOUCH': 'TOUCH',
  'Close settings': 'Einstellungen schließen',
  'RESUME': 'WEITER',
  '01 // VIDEO': '01 // GRAFIK',
  'QUALITY PRESET': 'QUALITÄTSSTUFE',
  'Changes render resolution and shadow filtering. High and Ultra add live car reflections, contact shadows and wet-road reflections.': 'Ändert Renderauflösung und Schattenfilterung. Hoch und Ultra bringen Live-Spiegelungen am Auto, Kontaktschatten und Reflexionen auf nasser Straße.',
  'LOW': 'NIEDRIG',
  'MEDIUM': 'MITTEL',
  'HIGH': 'HOCH',
  'ULTRA': 'ULTRA',
  'FRAME TARGET': 'BILDRATENZIEL',
  'Smooth targets 60 FPS; Battery caps rendering near 30 FPS.': 'Flüssig zielt auf 60 FPS, Akku begrenzt auf rund 30 FPS.',
  'SMOOTH // 60': 'FLÜSSIG // 60',
  'BATTERY // 30': 'AKKU // 30',
  'ADAPTIVE PERFORMANCE': 'ADAPTIVE LEISTUNG',
  'Responds only to sustained frame drops and recovers gradually.': 'Greift nur bei anhaltenden Rucklern ein und regelt schrittweise zurück.',
  'BLOOM': 'BLOOM',
  'Neon glow and bright highlights.': 'Neonglühen und helle Lichter.',
  'DYNAMIC SHADOWS': 'DYNAMISCHE SCHATTEN',
  'Disable this first if the frame rate drops.': 'Schalte das zuerst aus, wenn die Bildrate einbricht.',
  'PERSONAL BEST GHOST': 'BESTZEIT-GHOST',
  'Records the fastest lap on this device and races its non-colliding hologram.': 'Zeichnet deine schnellste Runde auf diesem Gerät auf und lässt dich gegen ihr kollisionsfreies Hologramm fahren.',
  'CAMERA SHAKE': 'KAMERAWACKELN',
  'HEAT HAZE': 'HITZEFLIMMERN',
  'Air shimmers above fires and blast shockwaves bend the view. Shockwaves stay off with reduced motion.': 'Luft flimmert über Feuer, Druckwellen verzerren die Sicht. Bei reduzierter Bewegung bleiben Druckwellen aus.',
  'SOFTEN FLASHES': 'BLITZE ABMILDERN',
  'Dims explosion flashes and blast lighting. Smoke, sparks and debris stay.': 'Dämpft Explosionsblitze und Lichteffekte. Rauch, Funken und Trümmer bleiben.',
  'AUTO DETECTION': 'AUTOMATISCHE ERKENNUNG',
  'MEASURING LIVE FRAME TIME…': 'MESSE LIVE-FRAMETIME …',
  '02 // AUDIO': '02 // AUDIO',
  '04 // DRIVING': '04 // FAHREN',
  'HANDLING ASSIST': 'FAHRHILFE',
  'Arcade softens steering near the limit, Balanced stays subtle, Pro leaves every input untouched. Slide recovery always comes from the shared stability control.': 'Arcade entschärft die Lenkung im Grenzbereich, Ausgewogen bleibt dezent, Profi lässt jede Eingabe unangetastet. Das Abfangen von Rutschern übernimmt immer die gemeinsame Stabilitätskontrolle.',
  'ARCADE': 'ARCADE',
  'BALANCED': 'AUSGEWOGEN',
  'PRO': 'PROFI',
  'CORNER COACH': 'KURVEN-COACH',
  'Shows a compact lift/brake cue before demanding corners and adds one optional haptic pulse.': 'Zeigt vor schwierigen Kurven einen kurzen Gas-weg-/Brems-Hinweis und gibt optional einen haptischen Impuls.',
  '05 // TOUCH + TILT': '05 // TOUCH + NEIGUNG',
  'THUMB LAYOUT': 'DAUMEN-LAYOUT',
  'Swap steering and action clusters for left-handed play.': 'Tauscht Lenk- und Aktionstasten für Linkshänder.',
  'LEFT-HANDED': 'LINKSHÄNDER',
  'BUTTON SIZE': 'TASTENGRÖSSE',
  'BUTTON OPACITY': 'TASTENDECKKRAFT',
  'OPTIONAL AUTO-GAS': 'OPTIONALES AUTO-GAS',
  'Keeps accelerating until BRAKE is pressed. Drag down on GO or BRAKE for analogue control.': 'Gibt Gas, bis du BREMSE drückst. Zieh auf LOS oder BREMSE nach unten für analoge Dosierung.',
  'HAPTIC FEEDBACK': 'HAPTISCHES FEEDBACK',
  'Short supported-device pulses for touch actions.': 'Kurze Vibrationsimpulse für Touch-Aktionen auf unterstützten Geräten.',
  'TILT STEERING': 'NEIGUNGSLENKUNG',
  'Checking motion sensors…': 'Prüfe Bewegungssensoren …',
  'MOBILE SENSOR': 'MOBILER SENSOR',
  'STEERING RANGE': 'LENKBEREICH',
  'NEUTRAL DEAD ZONE': 'NEUTRALE TOTZONE',
  'TILT RESPONSE': 'NEIGUNGSREAKTION',
  'Precise softens small movements; Agile reaches useful steering earlier.': 'Präzise dämpft kleine Bewegungen, Agil lenkt früher spürbar ein.',
  'PRECISE': 'PRÄZISE',
  'LINEAR': 'LINEAR',
  'AGILE': 'AGIL',
  'ENABLE TILT STEERING': 'NEIGUNGSLENKUNG AN',
  'CALIBRATE TILT': 'NEIGUNG KALIBRIEREN',
  '10-SECOND TILT SETUP': '10-SEKUNDEN-NEIGUNGSSETUP',
  'CENTRE 0%': 'MITTE 0%',
  'Hold the device naturally and keep it centred': 'Halte das Gerät natürlich und ruhig in der Mitte',
  'LEFT': 'LINKS',
  'ZERO': 'NULL',
  'RIGHT': 'RECHTS',
  'Live motion sensor diagnostics': 'Live-Diagnose der Bewegungssensoren',
  'LIVE SENSOR DIAGNOSTICS': 'LIVE-SENSORDIAGNOSE',
  'Useful on older Android devices': 'Hilfreich auf älteren Android-Geräten',
  'CONTEXT': 'KONTEXT',
  'CHECKING…': 'PRÜFE …',
  'RUNTIME': 'LAUFZEIT',
  'PERMISSION': 'BERECHTIGUNG',
  'NOT REQUESTED': 'NICHT ANGEFRAGT',
  'SOURCE': 'QUELLE',
  'NO SAMPLES': 'KEINE MESSWERTE',
  'AUTO AXIS': 'AUTO-ACHSE',
  'RAW VALUES': 'ROHWERTE',
  'TILT / ZERO': 'NEIGUNG / NULL',
  'STEERING': 'LENKUNG',
  'SAMPLES': 'MESSWERTE',
  'FULLSCREEN + LANDSCAPE': 'VOLLBILD + QUERFORMAT',
  'Phones and sensor-capable tablets default to tilt steering. The steering swiper appears whenever tilt is switched off or sensor data is unavailable: slide further to steer harder, let go to centre.': 'Handys und Tablets mit Sensoren lenken standardmäßig per Neigung. Ohne Neigung oder Sensordaten erscheint der Lenk-Slider: weiter wischen lenkt stärker, loslassen zentriert.',
  'RESET DEFAULTS': 'STANDARD WIEDERHERSTELLEN',
  'LEAVE RACE': 'RENNEN VERLASSEN',
  'RESUME RACE': 'WEITERFAHREN',
  'RESTART ↻': 'NEUSTART ↻',
  // Controls panel
  'Control scheme': 'Steuerungsschema',
  'NO CONTROLLER DETECTED': 'KEIN CONTROLLER ERKANNT',
  'Connect a controller and press any button. Wired and Bluetooth pads are detected automatically.': 'Schließ einen Controller an und drück eine beliebige Taste. Kabel- und Bluetooth-Pads werden automatisch erkannt.',
  'CONTROLLER': 'CONTROLLER',
  'KEYBOARD': 'TASTATUR',
  'PRESS ANY BUTTON': 'DRÜCK EINE BELIEBIGE TASTE',
  'to wake your controller': 'um deinen Controller zu wecken',
  'RAW STICK': 'STICK ROH',
  'OUTPUT': 'AUSGABE',
  'STEERING DEAD ZONE': 'LENK-TOTZONE',
  'STEERING RESPONSE': 'LENKANSPRECHEN',
  'TRIGGER DEAD ZONE': 'TRIGGER-TOTZONE',
  'VIBRATION': 'VIBRATION',
  'BUTTON MAP': 'TASTENBELEGUNG',
  'Select an action, then press the new button or move the stick. Taken buttons swap automatically.': 'Wähl eine Aktion, dann drück die neue Taste oder beweg den Stick. Belegte Tasten werden automatisch getauscht.',
  'INVERT STEERING': 'LENKUNG INVERTIEREN',
  'INVERT TURRET': 'TURM INVERTIEREN',
  'TEST VIBRATION': 'VIBRATION TESTEN',
  'RESET CONTROLLER MAP': 'CONTROLLER ZURÜCKSETZEN',
  'KEY MAP': 'TASTATURBELEGUNG',
  'Select an action and press the new key. The arrow keys stay available as secondary bindings.': 'Wähl eine Aktion und drück die neue Taste. Die Pfeiltasten bleiben als Zweitbelegung erhalten.',
  'SOUND': 'SOUND',
  'RESTART TRIAL': 'ZEITFAHREN NEU',
  'TELEMETRY': 'TELEMETRIE',
  'RESET KEYBOARD': 'TASTATUR ZURÜCKSETZEN',
  // Results overlay
  'RACE COMPLETE': 'RENNEN BEENDET',
  'FINAL ORDER': 'ENDSTAND',
  'RACE AGAIN ↻': 'NOCHMAL FAHREN ↻',
  'SHARE GHOST + QR ↗': 'GHOST + QR TEILEN ↗',
  'NEXT EVENT →': 'NÄCHSTES EVENT →',
  'BACK TO LOBBY': 'ZURÜCK ZUR LOBBY',
  'LEAVE SESSION': 'SITZUNG VERLASSEN',
  // Garage styling panel
  'LIVERY': 'LACKIERUNG',
  'One tap styles the whole car · fine-tune below': 'Ein Tipp stylt das ganze Auto · Feinschliff darunter',
  'Factory': 'Werk',
  'Roll a random, colour-matched build': 'Zufälligen, farblich abgestimmten Look würfeln',
  'Shuffle': 'Würfeln',
  'COLOURS': 'FARBEN',
  'Tap a swatch, or ＋ for any colour': 'Tipp auf ein Farbfeld oder ＋ für jede Farbe',
  'BODY KIT': 'BODYKIT',
  'Looks only · performance comes from the crew module': 'Nur Optik · Leistung kommt vom Crew-Modul',
  'Spoiler': 'Spoiler',
  'AERO': 'AERO',
  'Bikes run a fixed fairing.': 'Bikes haben eine feste Verkleidung.',
  'SPOILER': 'SPOILER',
  'WHEELS': 'FELGEN',
  'GRAPHICS': 'DESIGN',
  'STANCE': 'FAHRWERK',
  'SLAMMED': 'TIEF',
  'STOCK': 'SERIE',
  'LIFTED': 'HOCH',
  'RESET TO FACTORY': 'AUF WERKSZUSTAND',
  '{slot} colour': 'Farbe {slot}',
  'Custom colour': 'Eigene Farbe',
  'Custom {slot} colour': 'Eigene Farbe {slot}',
  'UNDERGLOW ON': 'UNTERBODEN AN',
  'UNDERGLOW OFF': 'UNTERBODEN AUS',
  'Unlocks at career level {level}': 'Freigeschaltet ab Karrierelevel {level}',
  'LV{level}': 'LV{level}',
  'BODY': 'KAROSSE',
  'ACCENT': 'AKZENT',
  'RIMS': 'FELGEN',
  'NEON': 'NEON',
  'NONE': 'KEINER',
  'DUCKTAIL': 'ENTENBÜRZEL',
  'GT WING': 'GT-FLÜGEL',
  '5-SPOKE': '5-SPEICHEN',
  'MESH': 'KREUZSPEICHE',
  'TURBINE': 'TURBINE',
  'CLEAN': 'OHNE',
  'CENTER': 'MITTE',
  'TWIN': 'DOPPEL',
  'CHEVRON': 'CHEVRON',
  'CIRCUIT': 'CIRCUIT',
  'Pearl white': 'Perlweiß',
  'Graphite': 'Graphit',
  'Race red': 'Rennrot',
  'Blaze orange': 'Glutorange',
  'Taxi yellow': 'Taxigelb',
  'Lagoon teal': 'Lagunentürkis',
  'Rally blue': 'Rallyeblau',
  'Ultraviolet': 'Ultraviolett',
  'Hot pink': 'Knallpink',
  'Field olive': 'Feldoliv',
  'Gloss black': 'Glanzschwarz',
  'White': 'Weiß',
  'Red': 'Rot',
  'Amber': 'Bernstein',
  'Acid lime': 'Säuregrün',
  'Cyan': 'Cyan',
  'Magenta': 'Magenta',
  'Violet': 'Violett',
  'Satin black': 'Seidenschwarz',
  'Gunmetal': 'Gunmetal',
  'Silver': 'Silber',
  'Gold': 'Gold',
  'Blue': 'Blau',
  'Lime': 'Limette',
  'Ice cyan': 'Eiscyan',
  'Acid': 'Acid',
  // Career: challenges, rewards, contracts
  'CHECKERED FLAG': 'ZIELFLAGGE',
  'Finish any race.': 'Beende ein beliebiges Rennen.',
  'PODIUM PRESSURE': 'PODIUMSDRUCK',
  'Finish in the top three against at least two rivals.': 'Komm gegen mindestens zwei Rivalen unter die ersten drei.',
  'STYLE HUNTER': 'STYLE-JÄGER',
  'Earn 2,500 style in one race.': 'Hol 2.500 Style in einem Rennen.',
  'CLEAN MACHINE': 'SAUBERE MASCHINE',
  'Finish with at least 85% integrity.': 'Komm mit mindestens 85 % Zustand ins Ziel.',
  'RISK TAKER': 'RISIKOFAHRER',
  'Hit an outside-line risk gate.': 'Triff ein Risiko-Tor auf der Außenlinie.',
  'CHAIN REACTION': 'KETTENREAKTION',
  'Reach a ×2.0 style multiplier.': 'Erreiche einen Style-Multiplikator von ×2,0.',
  'CHEVRON WRAP': 'CHEVRON-FOLIE',
  'ACID PULSE': 'ACID PULSE',
  'CIRCUIT WRAP': 'CIRCUIT-FOLIE',
  'GOLD RUSH': 'GOLDRAUSCH',
  'MIDNIGHT CHROME': 'MITTERNACHTSCHROM',
  'AURORA FLARE': 'POLARLICHT',
  'CITY ICON': 'STADTIKONE',
  'REDLINE PEARL': 'REDLINE-PERLMUTT',
  'TOUR LEGEND': 'TOUR-LEGENDE',
  'CLOCK IN': 'SCHICHTBEGINN',
  'Finish one race today.': 'Beende heute ein Rennen.',
  'FRONT ROW': 'ERSTE REIHE',
  'Take one podium against at least two rivals.': 'Hol ein Podium gegen mindestens zwei Rivalen.',
  'DAILY TAKEOVER': 'TAGESÜBERNAHME',
  'Win one race with at least one rival.': 'Gewinne ein Rennen gegen mindestens einen Rivalen.',
  'STYLE SHIFT': 'STYLE-SCHICHT',
  'Bank 3,000 cumulative style today.': 'Sichere heute insgesamt 3.000 Style.',
  'OUTSIDE BET': 'AUSSENSEITERWETTE',
  'Hit two risk lines today.': 'Triff heute zwei Risikolinien.',
  'CLEAN RUN': 'SAUBERER LAUF',
  'Finish once with at least 85% integrity.': 'Komm einmal mit mindestens 85 % Zustand ins Ziel.',
  'NIGHT REGULAR': 'STAMMGAST DER NACHT',
  'Finish five races this week.': 'Beende diese Woche fünf Rennen.',
  'PODIUM HABIT': 'PODIUMSGEWOHNHEIT',
  'Take three podiums this week.': 'Hol diese Woche drei Podien.',
  'DISTRICT CONTROL': 'REVIERKONTROLLE',
  'Win three races this week.': 'Gewinne diese Woche drei Rennen.',
  'STYLE ECONOMY': 'STYLE-ÖKONOMIE',
  'Bank 12,000 cumulative style.': 'Sichere insgesamt 12.000 Style.',
  'NO SAFE LINE': 'KEINE SICHERE LINIE',
  'Hit ten risk lines this week.': 'Triff diese Woche zehn Risikolinien.',
  'UNTOUCHABLE': 'UNANTASTBAR',
  'Finish clean three times this week.': 'Komm diese Woche dreimal sauber ins Ziel.',
  'PROVING GROUND': 'BEWÄHRUNGSPROBE',
  'Finish two races against PRO rivals.': 'Beende zwei Rennen gegen PROFI-Rivalen.',
  // Career: XP breakdown, ranks, track mastery
  'STREET CONTRACTS': 'STRASSENAUFTRÄGE',
  'LIVE CONTRACTS': 'LIVE-AUFTRÄGE',
  'TRACK MASTERY': 'STRECKENMEISTERSCHAFT',
  'WAGER // {name}': 'WETTE // {name}',
  'RACE FINISH': 'ZIELANKUNFT',
  'TIMED EVENT': 'ZEITEVENT',
  'FINISHING POSITION': 'ZIELPOSITION',
  '{difficulty} RIVALS': 'RIVALEN: {difficulty}',
  'ROOKIE': 'ROOKIE',
  'SPORT': 'SPORT',
  'STYLE BANK': 'STYLE-KONTO',
  'CLEAN FINISH': 'SAUBERE ZIELANKUNFT',
  'SURVIVAL': 'ÜBERLEBT',
  'RISK LINES': 'RISIKOLINIEN',
  'STYLE CHAIN': 'STYLE-KETTE',
  'RIVAL BOUNTY // {name}': 'RIVALENKOPFGELD // {name}',
  'STREETWISE': 'STRASSENKUNDIG',
  'NIGHT RUNNER': 'NACHTFAHRER',
  'APEX HUNTER': 'APEX-JÄGER',
  'ICON': 'IKONE',
  'NIGHT LEGEND': 'NACHTLEGENDE',
  'CITY HUNTER': 'STADTJÄGER',
  'REDLINE ELITE': 'REDLINE-ELITE',
  'FIRST FINISH': 'ERSTE ZIELANKUNFT',
  'FIVE FINISHES': 'FÜNF ZIELANKÜNFTE',
  'FIRST WIN': 'ERSTER SIEG',
  'THREE PODIUMS': 'DREI PODIEN',
  '2,500 STYLE': '2.500 STYLE',
  'AVALANCHE GALLERY': 'LAWINENGALERIE',
  'RIVIERA PANORAMA': 'RIVIERA-PANORAMA',
  'TIERGARTEN CUT': 'TIERGARTEN-ABKÜRZUNG',
  'LAUBEN ARCADE CUT': 'LAUBEN-ABKÜRZUNG',
  'HERRENHAUSEN PARK AXIS': 'HERRENHAUSEN-PARKACHSE',
  'MUSEGG SERVICE LANE': 'MUSEGG-LIEFERGASSE',
  'WATERS EDGE CUT': 'UFER-ABKÜRZUNG',
  'MIDNIGHT SERVICE CUT': 'MITTERNACHTS-SCHLEICHWEG',
  'QUARRY CUT': 'STEINBRUCH-ABKÜRZUNG',
  'DRY RIVER CUT': 'TROCKENFLUSS-ABKÜRZUNG',
  '3 ALPINE APEXES': '3 ALPEN-APEXE',
  '3 COAST APEXES': '3 KÜSTEN-APEXE',
  '2 CAPITAL APEXES': '2 HAUPTSTADT-APEXE',
  '2 OLD-TOWN APEXES': '2 ALTSTADT-APEXE',
  '2 LAKESIDE APEXES': '2 SEEUFER-APEXE',
  '2 QUAI APEXES': '2 QUAI-APEXE',
  '3 MOUNTAIN APEXES': '3 BERG-APEXE',
  '3 NEON APEXES': '3 NEON-APEXE',
  '3 CANYON APEXES': '3 CANYON-APEXE',
  '3 SUMMIT APEXES': '3 GIPFEL-APEXE',
  // Vehicle mastery
  'SHAKEDOWN': 'EINFAHREN',
  'Finish a race with this vehicle.': 'Beende ein Rennen mit diesem Fahrzeug.',
  'PROVEN': 'BEWÄHRT',
  'Win against at least one rival.': 'Gewinne gegen mindestens einen Rivalen.',
  'Land four perfect launches or slingshots.': 'Schaff vier perfekte Starts oder Slingshots.',
  'Take four perfect corners.': 'Fahr vier perfekte Kurven.',
  'Bank four perfect corners or clean passes.': 'Sichere vier perfekte Kurven oder saubere Überholmanöver.',
  'Catch four drifts for a drift boost.': 'Fang vier Drifts für einen Drift-Boost ab.',
  'Complete four alternate routes.': 'Fahr vier alternative Routen.',
  'Land four clean passes or takedowns.': 'Schaff vier saubere Überholmanöver oder Takedowns.',
  'Score two takedowns.': 'Hol zwei Takedowns.',
  'Land four scored jumps.': 'Lande vier gewertete Sprünge.',
  'Bank four routes or scored jumps.': 'Sichere vier Routen oder gewertete Sprünge.',
  'Build a six-action style chain four times.': 'Bau viermal eine Style-Kette aus sechs Aktionen auf.',
  // Night Tour
  'OLD TOWN OPENING': 'ALTSTADT-AUFTAKT',
  'A one-lap dawn sprint through Bern.': 'Ein Sprint über eine Runde durch Bern im Morgengrauen.',
  'LAKE PRESSURE': 'DRUCK AM SEE',
  'Three laps as the sun drops over Luzern.': 'Drei Runden, während die Sonne über Luzern versinkt.',
  'CITY STYLE LINE': 'STYLE-LINIE DER STADT',
  'Build the biggest chain around the Maschsee.': 'Bau die größte Kette rund um den Maschsee auf.',
  'CAPITAL HEAT': 'HAUPTSTADT-HITZE',
  'Bank your score before Mika takes Berlin.': 'Sichere deine Punkte, bevor Mika sich Berlin holt.',
  'FIRST ASCENT': 'ERSTBESTEIGUNG',
  'A cold dawn sprint through the alpine switchbacks.': 'Ein eisiger Sprint im Morgengrauen durch die Alpenkehren.',
  'VERTICAL LINE': 'VERTIKALE LINIE',
  'Own the sunset climb and commit to the summit route.': 'Beherrsch den Anstieg im Sonnenuntergang und geh voll auf die Gipfelroute.',
  'LAST ONE UP': 'WER ZULETZT OBEN IST',
  'Elimination pressure across the raised lake circuit.': 'Eliminationsdruck auf dem erhöhten Seekurs.',
  'ALPINE AFTERGLOW': 'ALPENGLÜHEN',
  'A clean lakeside finale with Lux waiting up front.': 'Ein sauberes Finale am See – Lux wartet schon vorne.',
  'PIRATE SIGNAL': 'PIRATENSIGNAL',
  'Build the loudest style chain under the neon towers.': 'Bau unter den Neontürmen die lauteste Style-Kette auf.',
  'DUST RUN': 'STAUBLAUF',
  'One lap, no excuses, through a live canyon storm.': 'Eine Runde, keine Ausreden – mitten durch einen Canyonsturm.',
  'COASTAL BLACKOUT': 'KÜSTEN-BLACKOUT',
  'Survive elimination traffic along the midnight coast.': 'Überleb den Eliminationsverkehr an der Küste um Mitternacht.',
  'NO LAST CALL': 'KEINE LETZTE RUNDE',
  'Bank the final Berlin heat before Nyx takes everything.': 'Sichere den letzten Berlin-Heat, bevor Nyx alles abräumt.',
  'EUROPEAN NIGHT RUN': 'EUROPÄISCHE NACHTFAHRT',
  'CITY LIGHTS PAINT': 'CITY-LIGHTS-LACK',
  'VERTICAL RIVAL SERIES': 'VERTIKALE RIVALENSERIE',
  'SUMMIT ICE PAINT': 'GIPFELEIS-LACK',
  'NO-RULES RADIO TOUR': 'PIRATENFUNK-TOUR OHNE REGELN',
  'OUTLAW VIOLET PAINT': 'OUTLAW-VIOLETT-LACK',
  // Toasts and player-facing errors (looked up at the display site)
  '{name} joined the grid': '{name} ist in der Startaufstellung',
  'A driver lost their link — their slot is being held.': 'Ein Fahrer hat die Verbindung verloren – sein Platz wird freigehalten.',
  '{message} — restoring the latest local checkpoint': '{message} – letzter lokaler Checkpoint wird geladen',
  '{message} — restarting the local event': '{message} – lokales Event startet neu',
  'Offline simulation could not start': 'Offline-Simulation konnte nicht starten',
  'Offline simulation worker stopped unexpectedly': 'Offline-Simulation wurde unerwartet beendet',
  'Offline simulation stopped unexpectedly': 'Offline-Simulation wurde unerwartet beendet',
  'Offline simulation data could not be read': 'Offline-Simulationsdaten konnten nicht gelesen werden',
  'Offline simulation sent malformed data': 'Offline-Simulation hat fehlerhafte Daten gesendet',
  'The offline race client could not process simulation data': 'Der Offline-Client konnte die Simulationsdaten nicht verarbeiten',
  'Add at least one NPC opponent to race offline, or enable Time Trial.': 'Füg mindestens einen KI-Gegner hinzu, um offline zu fahren, oder aktiviere Zeitfahren.',
  'The saved offline race is not compatible with this driver.': 'Das gespeicherte Offline-Rennen passt nicht zu diesem Fahrer.',
  'A newer NIGHTSHIFT build is required.': 'Eine neuere NIGHTSHIFT-Version wird benötigt.',
  'Race control did not respond in time.': 'Die Rennleitung hat nicht rechtzeitig geantwortet.',
  'Race control returned an invalid map set.': 'Die Rennleitung hat ungültige Strecken geliefert.',
  'Race control returned a stale or invalid Daily challenge.': 'Die Rennleitung hat eine veraltete oder ungültige Daily Challenge geliefert.',
  'The race client could not process server data': 'Der Renn-Client konnte die Serverdaten nicht verarbeiten',
  'The spectator client could not process server data': 'Der Zuschauer-Client konnte die Serverdaten nicht verarbeiten',
  'A valid ghost map, car and lap are required.': 'Für den Ghost werden eine gültige Strecke, ein Auto und eine Runde benötigt.',
  'The ghost does not include its exact lighting and weather setup.': 'Dem Ghost fehlen die genauen Licht- und Wettereinstellungen.',
  'The ghost needs at least 20 samples.': 'Der Ghost braucht mindestens 20 Messpunkte.',
  'Ghost metadata is too long.': 'Die Ghost-Metadaten sind zu lang.',
  'Truncated ghost challenge.': 'Unvollständige Ghost-Challenge.',
  'Invalid ghost challenge.': 'Ungültige Ghost-Challenge.',
  'An invite link is required to create a QR code.': 'Für den QR-Code wird ein Einladungslink benötigt.',
  'QR code generation failed.': 'QR-Code konnte nicht erstellt werden.',
  'QR code generation cancelled.': 'QR-Code-Erstellung abgebrochen.',
  'Local career save failed': 'Karriere konnte nicht lokal gespeichert werden',
  'Local career storage is unavailable': 'Lokaler Karrierespeicher ist nicht verfügbar',
  'Timed out waiting for local game-data lock': 'Zeitüberschreitung beim Zugriff auf die lokalen Spieldaten',
  'Lost local game-data lock while saving': 'Zugriff auf die lokalen Spieldaten beim Speichern verloren',
};
